
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
  DEFAULT_UNIT_TYPES,
  UnitNumber,
  UnitInputValue,
  UnitOptions,
  UnitType
} from "@/types/unit";
import { RangeOptions } from "@/types/controls";

import lodash, { isNumber } from "lodash";

import UnitDropdown from "@/components/Reusable/CustomInputs/UnitInput/UnitDropdown.vue";
import Slider from "@/components/Reusable/Slider.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import UnitCustomInputDropdown from "@/components/Reusable/CustomInputs/UnitInput/UnitCustomInputDropdown.vue";
import { getStyleForUnit, parseNumberAndUnitFromRawValue } from "@/util/unit";
import { EasyEvent } from "@/util/easy-event";
import { forceNextTick } from "@/util/force-next-tick";
import { uiStore } from "@/store/store-accessor";

@Component({
  components: {
    Slider,
    Dropdown,
    TextInput,
    NumberInput,
    UnitDropdown,
    UnitCustomInputDropdown
  }
})
export default class UnitInput extends Vue {
  @Prop({
    default: null
  })
  value!: UnitInputValue;

  @Prop()
  placeholder?: string;

  @Prop({ default: null })
  options!: UnitOptions | null;

  @Prop({ default: null })
  rangeOptions!: RangeOptions | null;

  @Prop({ default: false })
  disabled!: boolean;

  @Prop({ default: false })
  expanded!: boolean;

  rawValue: UnitNumber = "";
  dirty = false;

  // Drag Properties
  easyEvents = new EasyEvent();
  showDragHint = false;
  startDragX: number | null = null;
  initialDragValue = 0;

  get unitTypes(): UnitType[] {
    if (this.options?.types?.length) {
      return this.options?.types;
    }

    return DEFAULT_UNIT_TYPES;
  }

  get defaultType(): UnitType {
    const defaultSettingType = uiStore.settings.defaultUnitType || "px";
    const types = this.unitTypes;

    let fallbackType: UnitType | undefined;

    // If the default type is not in the list of available types, use the first one.
    if (types?.length) {
      fallbackType = types.includes(defaultSettingType)
        ? defaultSettingType
        : types[0];
    } else {
      fallbackType = defaultSettingType;
    }

    return this.options?.defaultType || fallbackType;
  }

  get shouldShowRangeSlider() {
    if (
      typeof this.rangeOptions?.min === "number" &&
      typeof this.rangeOptions?.max === "number" &&
      this.rangeOptions?.disableSlider !== true
    ) {
      // Only show range slider for the default unit. #1623
      return !this.value || this.value?.unit === this.defaultType;
    }

    return false;
  }

  get step() {
    return this.rangeOptions?.step || 1;
  }

  get internalNumber(): UnitNumber {
    return this.value?.number;
  }

  get internalUnit(): UnitType {
    return this.value?.unit || this.defaultType;
  }

  get isCustomCSS() {
    return this.internalUnit === "custom";
  }

  get canIncrement() {
    return !["custom", "auto"].includes(this.internalUnit);
  }

  get disableCustomUnitDropdown() {
    if (this.internalUnit !== "custom") {
      return true;
    }

    if (uiStore.settings.disableCustomUnitDropdown) {
      return true;
    }

    return this.options?.disableCustomUnitDropdown || false;
  }

  throttleUpdateNumber = lodash.throttle(this.updateNumber, 100);

  updateNumber(newNum: UnitNumber) {
    this.update(newNum, this.internalUnit);
  }

  updateUnit(unit: UnitType) {
    if (unit === "auto") {
      // Clear number if user selects `auto`
      this.update(null, unit);
      return;
    }

    this.update(this.internalNumber, unit);
  }

  update(number: UnitNumber, unit: UnitType | null) {
    if ((!unit || unit === this.defaultType) && !number && number !== 0) {
      // Clear the whole input if no unit or number are present.
      this.$emit("input", null);
      return;
    }

    const style = getStyleForUnit(number, unit);
    this.$emit("input", { number, unit, style });
  }

  adjustNumber(event: KeyboardEvent, isIncrement: boolean) {
    if (!this.canIncrement) return;

    this.dirty = false;

    const base = Number(this.internalNumber);
    const num = isNaN(base) ? 0 : base;

    let modifier = 1;
    if (event.metaKey) {
      modifier = 100;
    } else if (event.shiftKey) {
      modifier = 10;
    } else if (event.altKey) {
      modifier = 0.1;
    }

    const adjustment = isIncrement ? modifier : -modifier;

    this.updateNumber(Number((num + adjustment).toFixed(1)));
  }

  increment(event: KeyboardEvent) {
    this.adjustNumber(event, true);
  }

  decrement(event: KeyboardEvent) {
    this.adjustNumber(event, false);
  }

  @Watch("value.number", { immediate: true })
  onValueChange() {
    if (this.dirty) {
      this.dirty = false;
      return;
    }

    this.rawValue = isNumber(this.value?.number)
      ? `${this.value?.number}`
      : this.value?.number;
  }

  updateRawValue(newValue: string | null) {
    this.rawValue = newValue;

    // Disable unit switching for custom values
    if (this.internalUnit === "custom") {
      this.dirty = true;
      this.updateNumber(this.rawValue);
      return;
    }

    const [parsedNumber, parsedUnit, numMinusUnit] =
      parseNumberAndUnitFromRawValue(this.rawValue, this.unitTypes);

    const maybeNewUnit = parsedUnit || this.internalUnit;

    if (numMinusUnit) {
      this.rawValue = `${numMinusUnit}`;
      this.dirty = true;
    } else {
      this.dirty = false;
    }

    this.update(parsedNumber, maybeNewUnit);
  }

  // Dragging

  listenForAlt(event: MouseEvent) {
    this.showDragHint = event.altKey && this.internalUnit !== "custom";
    document.addEventListener("keydown", this.maybeShowDragHint);
    document.addEventListener("keyup", this.hideDragHint);
  }

  stopListeningForAlt() {
    if (this.startDragX !== null) {
      return;
    }

    document.removeEventListener("keydown", this.maybeShowDragHint);
    document.removeEventListener("keyup", this.hideDragHint);
  }

  hideDragHint(event: KeyboardEvent) {
    this.showDragHint = event.altKey;
  }

  maybeShowDragHint(event: KeyboardEvent) {
    if (
      event.altKey &&
      event.key !== "ArrowUp" &&
      this.internalUnit !== "custom"
    ) {
      this.showDragHint = true;
    }
  }

  onMaybeDrag(event: MouseEvent) {
    if (!event.altKey || this.internalUnit === "custom") {
      return;
    }

    document.body.classList.add("is-dragging-unit-input");

    this.startDragX = event.clientX;

    if (this.rawValue) {
      this.initialDragValue =
        typeof this.rawValue === "number"
          ? this.rawValue
          : parseFloat(this.rawValue || "0");
    } else {
      this.initialDragValue = 0;
    }

    this.easyEvents.on("mousemove", this.onDrag);
    this.easyEvents.on("mouseup", this.onStopDrag.bind(this));
  }

  // TODO: Types are not good enough, but fixing them is a bit tricky
  // and requires a lot of changes outside of this component.
  onDrag(event: MouseEvent) {
    const min = this.rangeOptions?.min as number;
    const max = this.rangeOptions?.max as number;

    if (this.startDragX === null) return;

    const deltaX = event.clientX - this.startDragX;
    const step = this.step as number;

    const newValue = Math.min(
      Math.max(
        this.initialDragValue + Math.round(deltaX / 5) * step,
        min ?? -Infinity
      ),
      max ?? Infinity
    );

    this.rawValue = `${newValue}`;
    this.throttleUpdateNumber(newValue);

    forceNextTick(() => {
      (this.$refs.input as HTMLElement)?.blur();
    });
  }

  onStopDrag() {
    this.startDragX = null;
    this.initialDragValue = 0;
    this.showDragHint = false;

    document.body.classList.remove("is-dragging-unit-input");
    this.stopListeningForAlt();

    this.easyEvents.off("mousemove", this.onDrag);
    this.easyEvents.off("mouseup", this.onStopDrag);
  }
}
